import { ReactNode, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import { useNavigate } from "react-router-dom";
import { FiMenu } from "react-icons/fi";
import BrandLogo from "../components/ui/BrandLogo";
import MessageBell from "../components/layout/MessageBell";
import UserMenu from "../components/layout/UserMenu";
import Footer from "../components/ui/Footer";
import Sidebar from "./Sidebar";

interface AppLayoutProps {
  children: ReactNode;
  title?: string;
  /** Form pages with a side panel need more horizontal room than regular pages */
  wide?: boolean;
  /** Name of the page, shown at the left of the top bar */
  crumb?: string;
  /** Replaces the width of the content, e.g. "max-w-[1200px]" */
  contentWidth?: string;
}

export default function AppLayout({
  children,
  title,
  wide = false,
  crumb,
  contentWidth,
}: AppLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated,
  );
  const navigate = useNavigate();

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen flex-col bg-white text-gray-900">
        <header className="flex items-center justify-center bg-gray-100 px-6 py-4 shadow">
          <button onClick={() => navigate("/home")}>
            <BrandLogo variant="dark" size="sm" />
          </button>
        </header>

        <main className="flex-1 p-6">{children}</main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-appBg text-gray-900">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Global top bar: the bell is reachable from every page */}
        <header className="flex h-[68px] items-center gap-3 border-b border-appLine bg-white px-4 md:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
            className="text-dantzBlue lg:hidden"
          >
            <FiMenu size={22} />
          </button>
          <button className="lg:hidden" onClick={() => navigate("/home")}>
            <BrandLogo variant="dark" size="sm" />
          </button>
          {crumb && (
            <span className="hidden text-sm text-appMuted lg:block">{crumb}</span>
          )}
          <div className="ml-auto flex items-center gap-3">
            <MessageBell />
            <UserMenu />
          </div>
        </header>

        <main className="flex-1 px-4 pb-16 pt-8 md:px-8 md:pt-10">
          <div
            className={`mx-auto w-full ${
              contentWidth ?? (wide ? "max-w-7xl" : "max-w-5xl")
            }`}
          >
            {title && (
              <h1 className="mb-6 font-heading text-2xl text-dantzBlue">
                {title}
              </h1>
            )}
            {children}
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
}
